import { PRINCIPALE, contesto, traduci } from '../i18n';

// Regole dei moduli di registrazione (clienti e collaboratori): usate dalle pagine e dai test
// (utils/registrazione.test.js). Il backend controlla di nuovo quello che conta (email, codici).
//
// I testi arrivano dai dizionari (sezione "accesso"). La lingua è sempre l'ULTIMO parametro e di base è
// l'italiano: il modulo del collaboratore (staff) chiama tutto senza lingua e resta in italiano com'era.

// La regola scritta sotto il campo e nell'errore (il server dice lo stesso: ERRORE_PASSWORD_DEBOLE in server.py)
const REGOLA_PASSWORD = traduci(PRINCIPALE, 'accesso.regola_password');

// Quattro tacche: lunghezza >= 8, lettere e numeri, una maiuscola, un simbolo (anche uno spazio in mezzo).
// Si accetta da "Buona" in su: 8 caratteri, almeno una lettera e almeno un altro criterio. Senza lettere
// ("        ", "........", "!!!!!!!!", "12345678") è sempre "Debole". Gli spazi agli estremi non contano.
const robustezzaPassword = (password = '', lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const p = String(password || '').trim();
  const lettera = /\p{L}/u.test(p);
  const criteri = {
    lunghezza: [...p].length >= 8, // caratteri veri (come len() sul server), non unità UTF-16
    lettereNumeri: lettera && /\d/.test(p),
    maiuscola: /\p{Lu}/u.test(p),
    speciale: /[^\p{L}\d]/u.test(p),
  };
  const tacche = Object.values(criteri).filter(Boolean).length;
  const livello = !criteri.lunghezza || !lettera || tacche <= 1 ? 'debole' : tacche === 4 ? 'ottima' : 'buona';
  let testo = t(`accesso.forza.${livello}`);
  if (!criteri.lunghezza) testo += ` · ${t('accesso.forza_almeno_8')}`;
  else if (!lettera) testo += ` · ${t('accesso.forza_serve_lettera')}`;
  else if (livello === 'debole') testo += ` · ${t('accesso.forza_aggiungete')}`;
  return { criteri, tacche, livello, testo, accettabile: livello !== 'debole' };
};

// Codice del consulente: 8 caratteri, maiuscolo, senza spazi né trattini
const normalizzaCodice = (valore) => (valore || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);

const emailValida = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email || '').trim());

// Telefono: solo cifre, spazi, +, -, parentesi e punti, con 6-15 cifre ("abc123def456" non passa)
const telefonoValido = (telefono) => {
  const t = (telefono || '').trim();
  const cifre = t.replace(/\D/g, '').length;
  return /^[\d\s+\-().]+$/.test(t) && cifre >= 6 && cifre <= 15;
};

// Lunghezze massime dei campi (maxLength negli input; il server rifiuta nomi oltre gli 80 caratteri)
const LUNGHEZZE = { nome: 80, cognome: 80, email: 254, telefono: 30 };

const erroriNome = (nome, cognome, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  if (!nome.trim()) return { campo: 'nome', testo: t('accesso.inserite_nome') };
  if (nome.trim().length > LUNGHEZZE.nome) return { campo: 'nome', testo: t('accesso.nome_lungo', { max: LUNGHEZZE.nome }) };
  if (!cognome.trim()) return { campo: 'cognome', testo: t('accesso.inserite_cognome') };
  if (cognome.trim().length > LUNGHEZZE.cognome) return { campo: 'cognome', testo: t('accesso.cognome_lungo', { max: LUNGHEZZE.cognome }) };
  return null;
};

// Primo errore del passo 1 del cliente (o null): { campo, testo }. Con un profilo già creato dal consulente
// (codice valido) nome e cognome sono quelli del profilo: non si controllano.
const erroriDatiCliente = ({ nome, cognome, email, telefono, codice }, { profiloGiaCreato = false } = {}, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const nomeSbagliato = profiloGiaCreato ? null : erroriNome(nome, cognome, lingua);
  if (nomeSbagliato) return nomeSbagliato;
  if (!emailValida(email)) return { campo: 'email', testo: t('accesso.email_incompleta') };
  if (!telefonoValido(telefono)) {
    const conAltriSegni = /[^\d\s+\-().]/.test((telefono || '').trim());
    return { campo: 'telefono', testo: t(conAltriSegni ? 'accesso.telefono_segni' : 'accesso.inserite_telefono') };
  }
  if (codice && codice.length !== 8) return { campo: 'codice', testo: t('accesso.codice_otto') };
  return null;
};

// Primo errore della password (o null); privacy: undefined se la casella non c'è
const erroriPassword = ({ password, conferma, privacy }, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  if (!robustezzaPassword(password, lingua).accettabile) {
    return { campo: 'password', testo: t('accesso.password_debole', { regola: t('accesso.regola_password') }) };
  }
  if (password !== conferma) return { campo: 'conferma', testo: t('accesso.password_diverse') };
  if (privacy === false) return { campo: 'privacy', testo: t('accesso.serve_privacy') };
  return null;
};

// Primo errore del modulo collaboratore (o null): lo compila lo staff, quindi resta in italiano
const erroriCollaboratore = ({ nome, cognome, email, codiceInvito, password, conferma }, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const nomeSbagliato = erroriNome(nome, cognome, lingua);
  if (nomeSbagliato) return nomeSbagliato;
  if (!emailValida(email)) return { campo: 'email', testo: t('accesso.email_incompleta') };
  if (!codiceInvito.trim()) return { campo: 'invito', testo: t('accesso.serve_invito') };
  return erroriPassword({ password, conferma }, lingua);
};

// Errore del backend in registrazione → passo in cui mostrarlo (nome, cognome, email, telefono e codice stanno nel
// passo 1) e campo da segnare
const campoDellErrore = (dettaglio) => {
  const d = dettaglio || '';
  if (/\bnome/i.test(d)) return 'nome'; // \b: "cognome" non conta come "nome"
  if (/cognome/i.test(d)) return 'cognome';
  if (/telefono/i.test(d)) return 'telefono';
  if (/email/i.test(d)) return 'email';
  if (/codice/i.test(d)) return 'codice';
  if (/password/i.test(d)) return 'password';
  return null;
};
const passoDellErrore = (dettaglio) => (['nome', 'cognome', 'telefono', 'email', 'codice'].includes(campoDellErrore(dettaglio)) ? 1 : 2);

export {
  REGOLA_PASSWORD, LUNGHEZZE, robustezzaPassword, normalizzaCodice, emailValida, telefonoValido, erroriDatiCliente,
  erroriPassword, erroriCollaboratore, campoDellErrore, passoDellErrore,
};
